import { useState } from "react";
import { C, PageHeader, GlassCard, Badge, Button, Skeleton, ProgressBar } from "@coremonit/design-system";
import { AlertTriangle, Sparkles, GitCommit, Activity, Database, Wifi, GitBranch, Settings, Rocket, Server } from "lucide-react";
import { useRCA, useServices } from "../api/hooks";

const CATEGORY_ICON: Record<string, React.ReactNode> = {
  deployment:     <Rocket size={12} />,
  traffic:        <Activity size={12} />,
  database:       <Database size={12} />,
  network:        <Wifi size={12} />,
  dependency:     <GitBranch size={12} />,
  config:         <Settings size={12} />,
  release:        <GitCommit size={12} />,
  infrastructure: <Server size={12} />,
};

export function RCAPage() {
  const services = useServices();
  const [serviceId, setServiceId] = useState<string | undefined>();
  const rca = useRCA(serviceId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader
        icon={<AlertTriangle size={16} />}
        title="Root Cause Analysis"
        subtitle="AI-ranked probable causes with evidence · linked metrics, traces, logs, and deployments"
        badge={<Badge color="purple"><Sparkles size={9} /> AI</Badge>}
      />
      <GlassCard style={{ padding: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ color: C.textMuted, fontSize: 11 }}>Service</span>
          <select
            value={serviceId ?? ""}
            onChange={(e) => setServiceId(e.target.value || undefined)}
            style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "6px 10px", fontSize: 12, minWidth: 260 }}
          >
            <option value="">Select a service…</option>
            {services.data?.items.map((s) => <option key={s.id} value={s.id} style={{ background: C.surface }}>{s.name}</option>)}
          </select>
        </div>
      </GlassCard>

      {!serviceId ? (
        <GlassCard style={{ padding: 40, textAlign: "center", color: C.textMuted, fontSize: 12 }}>Choose a service to run root-cause analysis.</GlassCard>
      ) : rca.isLoading ? (
        Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={140} radius={16} />)
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rca.data?.causes.map((c) => (
            <GlassCard key={c.rank} style={{ padding: 14 }} accent={c.rank === 1 ? C.borderBright : C.border}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 8, background: c.rank === 1 ? C.blueDim : C.surfaceHigh, border: `1px solid ${c.rank === 1 ? C.borderBright : C.border}`, color: c.rank === 1 ? C.blue : C.textSub, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>#{c.rank}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{c.title}</div>
                    <div style={{ color: C.textMuted, fontSize: 10, display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>{CATEGORY_ICON[c.category]}{c.category}</div>
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ color: c.confidencePct > 70 ? C.blue : c.confidencePct > 50 ? C.amber : C.textMuted, fontSize: 20, fontWeight: 700 }}>{c.confidencePct}%</div>
                  <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>Confidence</div>
                </div>
              </div>
              <div style={{ marginBottom: 10 }}><ProgressBar value={c.confidencePct} color={c.confidencePct > 70 ? C.blue : c.confidencePct > 50 ? C.amber : C.textSub} /></div>
              <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Evidence</div>
              <ul style={{ margin: 0, paddingLeft: 20, color: C.textSub, fontSize: 12, lineHeight: 1.6 }}>
                {c.evidence.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
              <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {c.linkedMetrics.map((m, i) => <LinkChip key={"m"+i} icon={<Activity size={9} />} label={m} />)}
                {c.linkedTraces.map((t, i) => <LinkChip key={"t"+i} icon={<GitBranch size={9} />} label={t} />)}
                {c.linkedDeployments.map((d, i) => <LinkChip key={"d"+i} icon={<Rocket size={9} />} label={d} />)}
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}

function LinkChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textSub, fontSize: 10, fontFamily: "ui-monospace, monospace" }}>
      {icon}{label}
    </span>
  );
}
